import { computed, inject, Service, signal } from '@angular/core';
import { UserSettingEventConsumerService } from '../../../events/user-setting/user-setting-event-consumer.service';
import { UserSettingEventProducerService } from '../../../events/user-setting/user-setting-event-producer.service';

/**
 * The symbols the trader has pinned on the market page.
 *
 * Pins are a stored user setting rather than component state: they must survive
 * leaving the page, closing the app and moving to another device, so they live
 * under `/userSettings` alongside the theme and the portfolio's eye toggle. This
 * service is the market page's view of that record — read {@link pinnedSymbols},
 * write through {@link toggle}.
 *
 * A pin applies without a confirmation, and the card is expected to change its
 * icon and jump to the top *as the trader taps* — so the new list is shown at
 * once and the write follows. Should the write fail, the display falls back to
 * what the API still holds rather than claiming a pin that was never saved.
 *
 * A burst of taps needs no handling here: the producer queues writes, so each
 * tap is saved in the order it was made, and each carries the full pin list as
 * it stood when the trader made it.
 */
@Service()
export class MarketPinsService {
	private readonly userSetting = inject(UserSettingEventConsumerService);
	private readonly userSettingProducer = inject(UserSettingEventProducerService);

	/**
	 * The list a tap produced, held only until its write settles. `null` means
	 * nothing is in flight and the stored setting speaks for itself.
	 */
	private readonly pending = signal<readonly string[] | null>(null);

	/**
	 * Pinned isins: the tap the trader just made if one is still being saved,
	 * otherwise the stored setting. Reading the setting from the event bus means
	 * the cached copy already orders the first paint of the next session, before
	 * the API answers.
	 */
	readonly pinnedSymbols = computed<readonly string[]>(
		() => this.pending() ?? this.userSetting.getSetting().market.pinnedSymbols
	);

	isPinned(isin: string): boolean {
		return this.pinnedSymbols().includes(isin);
	}

	/**
	 * Pin an unpinned symbol, or unpin a pinned one.
	 *
	 * A new pin goes to the end of the stored list. The list is a set of choices,
	 * not an ordering — the market page orders the pinned group by the traded
	 * value the API sorted on — so where an entry sits in it does not matter.
	 *
	 * @returns `true` when the change was persisted, `false` when the write failed
	 * and the display fell back to the stored pins.
	 */
	toggle(isin: string): Promise<boolean> {
		const current = this.pinnedSymbols();
		const next = current.includes(isin) ? current.filter(pinned => pinned !== isin) : [...current, isin];

		this.pending.set(next);

		return this.userSettingProducer.savePinnedSymbols(next).then(saved => {
			// Only the tap still on screen hands the display back to the stored
			// setting: a later tap has already replaced this list, and its own
			// write decides when it is done.
			if (this.pending() === next) {
				this.pending.set(null);
			}

			return saved;
		});
	}
}
